// Dates are stored and sent to the server as ISO text ("YYYY-MM-DD", plus " HH:mm" in plain sheets)
// but shown in the browser's locale date format - the way Excel shows a date cell in the format of
// the user's system settings (08/31/2019 in the US, 31/08/2019 in the UK, 31.08.2019 in Germany...).

const LOCALE = (typeof navigator !== 'undefined' && (navigator.languages?.[0] || navigator.language)) || 'en-US';

let formatter;
try {
    formatter = new Intl.DateTimeFormat(LOCALE, { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC', numberingSystem: 'latn' });
} catch (error) {
    formatter = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC' });
}

const SAMPLE = new Date(Date.UTC(2019, 7, 31));
// Order of the parts in the locale's format, e.g. ['m', 'd', 'y']
const ORDER = (() => {
    const order = formatter.formatToParts(SAMPLE)
        .filter(part => ['year', 'month', 'day'].includes(part.type))
        .map(part => part.type[0]);
    return order.length === 3 && new Set(order).size === 3 ? order : ['y', 'm', 'd'];
})();

// How 2019-08-31 looks in this browser, for hints like "use the format 08/31/2019"
const DATE_EXAMPLE = formatter.format(SAMPLE);

// The locale format as a jSuites calendar format ("MM/DD/YYYY", "DD.MM.YYYY"...), for date pickers
const CALENDAR_FORMAT = (() => {
    const tokens = { year: 'YYYY', month: 'MM', day: 'DD' };
    const format = formatter.formatToParts(SAMPLE)
        .map(part => tokens[part.type] || (part.type === 'literal' ? part.value : ''))
        .join('');
    return /^[YMD/.\- ]+$/.test(format) && ['YYYY', 'MM', 'DD'].every(t => format.includes(t)) ? format : 'YYYY-MM-DD';
})();

const ISO_RE =/^(\d{4})-(\d{2})-(\d{2})(?: (\d{2}):(\d{2}))?$/;
const TIME_SUFFIX_RE = /(?:\s+|T)([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/;

function isoDay(y, m, d) {
    const year = Number(y), month = Number(m), day = Number(d);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (isNaN(date) || date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
    return date.toISOString().slice(0, 10);
}

// "2019-08-31" -> "08/31/2019" (locale); "2019-08-31 14:05" -> "08/31/2019 14:05".
// Anything that isn't a stored ISO date is returned unchanged.
function formatIsoDate(value) {
    if (value === null || value === undefined) return '';
    const text = value.toString();
    const match = ISO_RE.exec(text);
    if (!match || !isoDay(match[1], match[2], match[3])) return text;
    const date = formatter.format(new Date(`${match[1]}-${match[2]}-${match[3]}T00:00:00.000Z`));
    return match[4] ? `${date} ${match[4]}:${match[5]}` : date;
}

// A Date / timestamp holding a calendar day at UTC midnight (DateOfBirth...) -> locale text
function formatUtcDate(value) {
    if (!value) return '';
    const date = new Date(value);
    return isNaN(date) ? '' : formatter.format(date);
}

// Text typed in the grid -> ISO ("YYYY-MM-DD" or "YYYY-MM-DD HH:mm"), or null if it isn't a full date.
// Accepts the locale format, ISO, and year-first input (2019/08/31); the year must have 4 digits
// so short numbers like "1.2" are never taken for dates.
function parseDisplayDate(value, { allowTime = false } = {}) {
    if (value === null || value === undefined) return null;
    // Some locales (Arabic, Hebrew) put direction marks into the formatted date
    let text = value.toString().replace(/[\u200e\u200f\u061c]/g, '').trim();
    let time = '';
    const timeMatch = TIME_SUFFIX_RE.exec(text);
    if (timeMatch && timeMatch.index > 0) {
        if (!allowTime) return null;
        time = ` ${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
        text = text.slice(0, timeMatch.index).trim();
    }
    const match = /^(\d{1,4})[-/.]\s*(\d{1,2})[-/.]\s*(\d{1,4})\.?$/.exec(text);
    if (!match) return null;
    const tokens = [match[1], match[2], match[3]];
    const order = tokens[0].length === 4 ? ['y', 'm', 'd'] : ORDER;
    const parts = {};
    order.forEach((key, i) => { parts[key] = tokens[i]; });
    if (!parts.y || parts.y.length !== 4 || parts.m.length > 2 || parts.d.length > 2) return null;
    const iso = isoDay(parts.y, parts.m, parts.d);
    return iso ? iso + time : null;
}

module.exports = {
    CALENDAR_FORMAT,
    DATE_EXAMPLE,
    formatIsoDate,
    formatUtcDate,
    parseDisplayDate,
};
